import { PnlSnapshotCache } from 'src/components/star-grid/services/pnlSnapshotCache';
import { hasPnlDisplayMetadata, needsPnlDisplayMetadata, withPnlDisplayMetadata } from 'src/components/star-grid/utils/pnlDisplayMetadata';
import { PnlReportStatusError } from 'src/components/star-grid/utils/pnlReportStatusError';
import { getPnlReportConfig, queryPnlReport } from 'src/components/star-grid/services/starGridService';
import { PnlReportConfig, PnlPivotModel, PnlQueryResponse } from 'src/components/star-grid/starGridTypes';
import { buildPnlPivotModel } from 'src/components/star-grid/utils/pnlPivot';
import { defaultReportSelection, pnlReportRequest, PnlReportSelection } from 'src/components/star-grid/utils/pnlReportState';

export interface LoadedPnlReport extends PnlReportSelection {
  readonly definition: PnlReportConfig;
  readonly response?: PnlQueryResponse;
  readonly model?: PnlPivotModel;
}

interface LoadOptions {
  readonly reportId: number;
  readonly asOfTimestamp?: string;
  readonly definition?: PnlReportConfig;
  readonly selection?: PnlReportSelection;
  readonly signal: AbortSignal;
  readonly snapshotCache?: PnlSnapshotCache;
  readonly bypassSnapshotCache?: boolean;
  readonly presentation?: PnlQueryResponse;
  readonly onDefinition?: (definition: PnlReportConfig, selection: PnlReportSelection) => void;
}

const historicalPresentation = async (
  reportId: number,
  definition: PnlReportConfig,
  selection: PnlReportSelection,
  response: PnlQueryResponse,
  signal: AbortSignal,
  presentation?: PnlQueryResponse
): Promise<PnlQueryResponse> => {
  if (!selection.asOfTimestamp || !needsPnlDisplayMetadata(response)) return response;
  const reference = hasPnlDisplayMetadata(presentation)
    ? presentation!
    : await queryPnlReport(reportId, pnlReportRequest(definition, { ...selection, asOfTimestamp: undefined }), signal);
  return withPnlDisplayMetadata(response, reference);
};

export const loadPnlReport = async ({
  reportId,
  asOfTimestamp,
  definition: cached,
  selection: chosen,
  signal,
  snapshotCache,
  bypassSnapshotCache,
  presentation,
  onDefinition
}: LoadOptions): Promise<LoadedPnlReport> => {
  const definition = cached ?? (await getPnlReportConfig(reportId, signal));
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  const selection = chosen ?? { ...defaultReportSelection(definition), asOfTimestamp };
  onDefinition?.(definition, selection);
  if (definition.status !== 'READY') return { definition, ...selection, response: undefined, model: undefined };
  const request = pnlReportRequest(definition, selection);
  const query = snapshotCache
    ? snapshotCache.query({ reportId, request, definition, signal, bypassCache: bypassSnapshotCache })
    : queryPnlReport(reportId, request, signal);
  const queried = await query.catch((error: unknown) => {
    if (error instanceof PnlReportStatusError && error.reportStatus) return error;
    throw error;
  });
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  if (queried instanceof PnlReportStatusError) return { definition: { ...definition, status: queried.reportStatus! }, ...selection };
  const response = await historicalPresentation(reportId, definition, selection, queried, signal, presentation);
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  const model = buildPnlPivotModel(response, definition.scenarios, definition.variance_pairs);
  return { definition, ...selection, response, model };
};
